import React, { useState } from "react";
import { CHAINS } from "../config/chains";
import { copyTextToClipboard } from "../utils/inviteLink";

// Palette mirrors TransferRequestModal / EditorialForm (light/cream theme).
const MONO = "'JetBrains Mono', ui-monospace, Menlo, monospace";
const SANS = "'Space Grotesk', -apple-system, sans-serif";
const SERIF = "'Instrument Serif', Georgia, serif";
const INK = "#16181A";
const INK_SOFT = "#8E8E88";
const RULE = "#E6E6E1";
const RULE_SOFT = "#F2F2EE";
const ACCENT = "#56AEF0";
const ACCENT_INK = "#08222F";

function short(addr) {
  const a = String(addr || "");
  return a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a;
}

function Row({ label, children }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 12,
        padding: "11px 0",
        borderBottom: `1px solid ${RULE_SOFT}`,
      }}
    >
      <span style={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase", color: INK_SOFT }}>
        {label}
      </span>
      <span style={{ fontFamily: MONO, fontSize: 13, color: INK, textAlign: "right", wordBreak: "break-all" }}>
        {children}
      </span>
    </div>
  );
}

/**
 * Recipient-facing payment-request popup. Shown when the app opens from an incoming
 * request link. Displays network / token / amount / note / recipient address with a
 * Pay button. Pay → sign in on that chain + token → onboarding (if the payer has no
 * account there yet) → the transfer form arrives pre-filled.
 */
export default function PaymentRequestModal({ request, onPay, onDecline }) {
  const [addrCopied, setAddrCopied] = useState(false);
  if (!request) return null;

  const copyAddr = async () => {
    try {
      await copyTextToClipboard(request.to);
      setAddrCopied(true);
      setTimeout(() => setAddrCopied(false), 1500);
    } catch {}
  };

  const chain = CHAINS[request.chainKey];
  const token = chain?.tokens?.[request.tokenId];
  const chainName = request.chainName || chain?.name || request.chainKey || "—";
  const network = request.network || chain?.network || "";
  const symbol = request.tokenSymbol || token?.symbol || String(request.tokenId || "").toUpperCase();

  return (
    <div
      onClick={onDecline}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 4100,
        background: "rgba(20,22,24,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 420,
          maxHeight: "92vh",
          overflowY: "auto",
          background: "#fff",
          border: `1px solid ${RULE}`,
          borderRadius: 0,
          padding: 24,
          boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
          fontFamily: SANS,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontFamily: SERIF, fontSize: 26, fontWeight: 400, color: INK, lineHeight: 1.1 }}>Payment Request</div>
            <div style={{ fontFamily: MONO, fontSize: 11.5, color: INK_SOFT, marginTop: 5 }}>
              Request for a confidential payment.
            </div>
          </div>
          <button
            onClick={onDecline}
            aria-label="Close"
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, lineHeight: 1, color: INK_SOFT, padding: 4 }}
          >
            ×
          </button>
        </div>

        {/* Amount hero */}
        <div style={{ textAlign: "center", padding: "22px 0 16px" }}>
          <div style={{ fontFamily: SANS, fontSize: 34, fontWeight: 700, color: INK, lineHeight: 1.1, wordBreak: "break-all" }}>
            {request.amount} <span style={{ fontSize: 20, color: INK_SOFT, fontWeight: 600 }}>{symbol}</span>
          </div>
        </div>

        <div>
          <Row label="Network">
            {chainName}
            {network ? <span style={{ color: INK_SOFT }}> ({network})</span> : null}
          </Row>
          <Row label="Token">{symbol}</Row>
          <Row label="To">
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              {short(request.to)}
              <button
                type="button"
                onClick={copyAddr}
                aria-label="Copy address"
                title={addrCopied ? "Copied" : "Copy address"}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: addrCopied ? ACCENT : INK_SOFT,
                  padding: 0,
                }}
              >
                {addrCopied ? (
                  <span style={{ fontSize: 11, fontFamily: MONO }}>Copied</span>
                ) : (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                )}
              </button>
            </span>
          </Row>
        </div>

        {request.note ? (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase", color: INK_SOFT, marginBottom: 6 }}>
              Note
            </div>
            <div
              style={{
                fontFamily: SANS,
                fontSize: 14,
                color: INK,
                background: "#fff",
                border: `1px solid ${RULE}`,
                padding: "12px 14px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {request.note}
            </div>
          </div>
        ) : null}

        <button
          onClick={onPay}
          style={{
            marginTop: 22,
            width: "100%",
            padding: "14px 16px",
            borderRadius: 999,
            border: "none",
            cursor: "pointer",
            background: ACCENT,
            color: ACCENT_INK,
            fontFamily: SANS,
            fontWeight: 700,
            fontSize: 16,
          }}
        >
          Pay {request.amount} {symbol}
        </button>
        <div style={{ fontFamily: MONO, fontSize: 10.5, color: INK_SOFT, marginTop: 10, textAlign: "center" }}>
          You'll sign in on {chainName} to continue.
        </div>
      </div>
    </div>
  );
}
